/**
 * Профайлер для I18nKernelLoader - відображення мовних файлів
 */

import { EyeOutlined, GlobalOutlined, ReloadOutlined, SearchOutlined } from '@ant-design/icons'
import { Button, Col, Input, Modal, Row, Select, Space, Table, Tag, Typography, message } from 'antd'
import React, { useMemo, useState } from 'react'
import { Kernel } from '../../../Kernel'
import { DebugProfilerData, DebugProfilerInterface } from '../types'
import { JsonViewer } from '../components/JsonViewer'

const { Text } = Typography

// Компонент для відображення JSON перекладу у модальному вікні
const TranslationJsonModal: React.FC<{
    translations: any
    language: string
    namespace: string
}> = ({ translations, language, namespace }) => {
    const [isVisible, setIsVisible] = useState(false)

    const jsonStr = JSON.stringify(translations, null, 2)

    const copyToClipboard = () => {
        navigator.clipboard
            .writeText(jsonStr)
            .then(() => {
                message.success('JSON скопійовано!')
            })
            .catch((err) => {
                console.error('Помилка копіювання:', err)
            })
    }

    return (
        <>
            <Button
                type="link"
                size="small"
                icon={<EyeOutlined />}
                onClick={() => setIsVisible(true)}
                style={{ padding: 0, height: 'auto' }}
            >
                Перегляд
            </Button>
            <Modal
                title={`Переклади: ${language} / ${namespace}`}
                open={isVisible}
                onCancel={() => setIsVisible(false)}
                footer={[
                    <Button key="copy" onClick={copyToClipboard}>
                        Копіювати JSON
                    </Button>,
                    <Button key="close" type="primary" onClick={() => setIsVisible(false)}>
                        Закрити
                    </Button>,
                ]}
                width={900}
                styles={{ body: { padding: '16px' } }}
            >
                <JsonViewer data={translations} showSearch={true} />
            </Modal>
        </>
    )
}

// Допоміжні функции для підрахунку ключів
const countKeys = (obj: any): number => {
    let count = 0
    for (const key in obj) {
        if (typeof obj[key] === 'object' && obj[key] !== null) {
            count += countKeys(obj[key])
        } else {
            count++
        }
    }
    return count
}

const collectKeys = (obj: any, prefix: string = ''): string[] => {
    const keys: string[] = []
    for (const key in obj) {
        const fullKey = prefix ? `${prefix}.${key}` : key
        if (typeof obj[key] === 'object' && obj[key] !== null) {
            keys.push(...collectKeys(obj[key], fullKey))
        } else {
            keys.push(fullKey)
        }
    }
    return keys
}

// Компонент для відображення таблиці перекладів
const TranslationsTable: React.FC<{ resources: any; namespaces: string[]; onRefresh?: () => void }> = ({
    resources,
    namespaces,
    onRefresh,
}) => {
    const [searchText, setSearchText] = useState('')
    const [languageFilter, setLanguageFilter] = useState<string | undefined>(undefined)
    const [namespaceFilter, setNamespaceFilter] = useState<string | undefined>(undefined)

    const languages = Object.keys(resources)

    // Перетворюємо дані для відображення у таблиці
    const translationsArray = useMemo(() => {
        const result: any[] = []

        namespaces.forEach((ns) => {
            languages.forEach((lang) => {
                const nsData = resources[lang]?.[ns]
                if (nsData) {
                    result.push({
                        key: `${lang}-${ns}`,
                        language: lang,
                        namespace: ns,
                        keysCount: countKeys(nsData),
                        translations: nsData,
                        hasData: true,
                    })
                }
            })
        })

        return result
    }, [resources, namespaces, languages])

    // Фільтрація даних
    const filteredTranslations = useMemo(() => {
        return translationsArray.filter((item) => {
            const searchMatch =
                searchText === '' ||
                item.language.toLowerCase().includes(searchText.toLowerCase()) ||
                item.namespace.toLowerCase().includes(searchText.toLowerCase())

            const languageMatch = languageFilter === undefined || item.language === languageFilter
            const namespaceMatch = namespaceFilter === undefined || item.namespace === namespaceFilter

            return searchMatch && languageMatch && namespaceMatch
        })
    }, [translationsArray, searchText, languageFilter, namespaceFilter])

    const columns = [
        {
            title: 'Мова',
            dataIndex: 'language',
            key: 'language',
            width: '15%',
            render: (language: string) => (
                <Tag color={language === 'uk' ? 'blue' : language === 'en' ? 'green' : 'orange'}>
                    {language.toUpperCase()}
                </Tag>
            ),
        },
        {
            title: 'Namespace',
            dataIndex: 'namespace',
            key: 'namespace',
            width: '25%',
            render: (namespace: string) => (
                <Text code style={{ fontSize: '12px' }}>
                    {namespace}
                </Text>
            ),
        },
        {
            title: 'Кількість ключів',
            dataIndex: 'keysCount',
            key: 'keysCount',
            width: '15%',
            align: 'center' as const,
            render: (count: number) => <Tag color="processing">{count}</Tag>,
        },
        {
            title: 'Дії',
            key: 'actions',
            width: '10%',
            render: (_: any, record: any) => (
                <TranslationJsonModal
                    translations={record.translations}
                    language={record.language}
                    namespace={record.namespace}
                />
            ),
        },
    ]

    return (
        <Space orientation="vertical" style={{ width: '100%' }}>
            {/* Фільтри */}
            <Row gutter={16}>
                <Col span={6}>
                    <Input
                        placeholder="Пошук по мові або namespace..."
                        prefix={<SearchOutlined />}
                        value={searchText}
                        onChange={(e) => setSearchText(e.target.value)}
                        allowClear
                    />
                </Col>
                <Col span={6}>
                    <Select
                        placeholder="Фільтр по мові"
                        value={languageFilter}
                        onChange={setLanguageFilter}
                        allowClear
                        style={{ width: '100%' }}
                        options={languages.map((lang) => ({
                            label: lang.toUpperCase(),
                            value: lang,
                        }))}
                    />
                </Col>
                <Col span={6}>
                    <Select
                        placeholder="Фільтр по namespace"
                        value={namespaceFilter}
                        onChange={setNamespaceFilter}
                        allowClear
                        style={{ width: '100%' }}
                        options={namespaces.map((ns) => ({
                            label: ns,
                            value: ns,
                        }))}
                    />
                </Col>
                {onRefresh && (
                    <Col span={6}>
                        <Button icon={<ReloadOutlined />} onClick={onRefresh}>
                            Оновити
                        </Button>
                    </Col>
                )}
            </Row>

            {/* Таблиця */}
            <Table
                columns={columns}
                dataSource={filteredTranslations}
                pagination={{
                    pageSize: 20,
                    showSizeChanger: true,
                    showQuickJumper: true,
                    showTotal: (total, range) => `${range[0]}-${range[1]} з ${total} перекладів`,
                }}
                size="small"
                scroll={{ y: 400 }}
            />
        </Space>
    )
}

// Основний профайлер
export const I18nLoaderProfiler: DebugProfilerInterface = {
    key: 'i18n-loader',
    name: 'I18n',
    icon: <GlobalOutlined />,
    priority: 8,

    async getData(onRefresh?: () => void): Promise<DebugProfilerData> {
        try {
            // Отримуємо загрузчик i18n з kernel
            const kernel = Kernel.getInstance()
            const i18nLoader = kernel.getLoader('i18n') as any

            if (!i18nLoader) {
                return {
                    summary: {
                        status: 'warning',
                        label: 'I18n загрузчик не знайдено',
                    },
                    details: {
                        stats: [
                            {
                                title: 'Статус',
                                value: 'I18n загрузчик не зареєстровано в kernel',
                                color: '#faad14',
                            },
                        ],
                    },
                }
            }

            // Отримуємо дані з уже завантаженого лоадера
            const resources = i18nLoader.getResources ? i18nLoader.getResources() : {}
            const namespaces = i18nLoader.getNamespaces ? i18nLoader.getNamespaces() : []
            const languages = i18nLoader.getAvailableLanguages ? i18nLoader.getAvailableLanguages() : []

            if (Object.keys(resources).length === 0) {
                return {
                    summary: {
                        status: 'warning',
                        label: 'Немає даних',
                    },
                    details: {
                        stats: [
                            {
                                title: 'Статус',
                                value: 'I18n дані не завантажені',
                                color: '#faad14',
                            },
                        ],
                    },
                }
            }

            // Підрахунок загальної статистики
            const totalKeys = namespaces.reduce((total: number, ns: string) => {
                return (
                    total +
                    languages.reduce((nsTotal: number, lang: string) => {
                        const nsData = resources[lang]?.[ns]
                        if (nsData) {
                            return nsTotal + countKeys(nsData)
                        }
                        return nsTotal
                    }, 0)
                )
            }, 0)

            return {
                summary: {
                    count: totalKeys,
                    status: totalKeys > 0 ? 'success' : 'warning',
                    label: `${totalKeys} перекладів`,
                },
                details: {
                    custom: onRefresh ? (
                        <TranslationsTable resources={resources} namespaces={namespaces} onRefresh={onRefresh} />
                    ) : (
                        <TranslationsTable resources={resources} namespaces={namespaces} />
                    ),
                },
            }
        } catch (error) {
            console.error('Помилка отримання даних I18n профайлера:', error)
            return {
                summary: {
                    status: 'error',
                    label: 'Помилка',
                },
                details: {
                    stats: [
                        {
                            title: 'Помилка',
                            value: error instanceof Error ? error.message : 'Невідома помилка',
                            color: '#ff4d4f',
                        },
                    ],
                },
            }
        }
    },

    isEnabled(): boolean {
        try {
            const kernel = Kernel.getInstance()
            const i18nLoader = kernel.getLoader('i18n')
            return !!i18nLoader
        } catch {
            return false
        }
    },
}
